import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { BenefitClaim, BenefitType } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { ErrorNote, Field, PageHeader, Spinner } from '../../ui/components';

/** Plain-language bullets built only from the real, structured rule fields the API returns — nothing invented. */
function describeRules(type: BenefitType): string[] {
  const lines: string[] = [];
  const e = type.eligibilityRules;
  const d = type.durationRules;
  if (e.minAgeYears !== undefined) lines.push(`Minimum age ${e.minAgeYears}`);
  if (e.maxAgeYears !== undefined) lines.push(`Maximum age ${e.maxAgeYears}`);
  if (e.requiresEmployerCert) lines.push('Requires employer certification of wage cessation');
  if (e.requiresMedicalCert) lines.push('Requires a medical certificate');
  if (e.requiresDeathEvent) lines.push('Requires a registered death event');
  if (d.certDays) lines.push(`Paid per certified day${d.maxDays ? ` (up to ${d.maxDays} days)` : ''}`);
  if (d.fixedDays) lines.push(`Fixed duration of ${d.fixedDays} days`);
  if (d.untilAge) lines.push(`Payable until age ${d.untilAge}`);
  if (d.indefinite) lines.push('Payable indefinitely while conditions hold');
  if (type.medicalBoardRequired) lines.push('Referred to the Medical Board before a decision is made');
  return lines;
}

export function Apply() {
  const { persona } = useAuth();
  const navigate = useNavigate();
  const typesQ = useApiQuery<{ items: BenefitType[] }>('/api/v1/benefits/types');
  const [selected, setSelected] = useState<BenefitType | null>(null);
  const [dob, setDob] = useState('');
  const [evidence, setEvidence] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<BenefitClaim | null>(null);

  const submit = async () => {
    if (!selected || !persona?.nin) return;
    setBusy(true);
    setError(null);
    try {
      const claim = await api.post<BenefitClaim>('/api/v1/benefits/claims', {
        nin: persona.nin,
        dob,
        benefitTypeCode: selected.code,
        evidenceRefs: evidence
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
      });
      setSuccess(claim);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not submit your claim');
    } finally {
      setBusy(false);
    }
  };

  if (success) {
    return (
      <div>
        <PageHeader title="Claim submitted" />
        <div className="card px-5 py-6 text-center">
          <div className="text-3xl">✓</div>
          <p className="mt-2 text-sm font-semibold text-slate-900">
            Your claim {success.refNumber} for {selected?.name} has been submitted.
          </p>
          <p className="mt-1 text-sm text-slate-500">
            It's now under evidence check. An ASP benefits officer will review it{selected?.medicalBoardRequired ? ' and may refer it to the Medical Board' : ''}.
          </p>
          <button className="btn-primary mt-4" onClick={() => navigate('/citizen')}>
            View in My Benefits
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Apply for a benefit" subtitle="Statutory benefits under the Social Security Act — pick the one that fits your situation" />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      {typesQ.loading ? <Spinner label="Loading benefit types…" /> : null}
      {typesQ.error ? <ErrorNote message={typesQ.error} /> : null}

      {!selected ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {(typesQ.data?.items ?? []).map((t) => {
            const latestRate = t.rates?.[0];
            return (
              <button
                key={t.id}
                onClick={() => setSelected(t)}
                className="card p-4 text-left transition hover:border-brand-500 hover:shadow-md"
              >
                <div className="font-semibold text-slate-900">{t.name}</div>
                <div className="mt-0.5 text-xs font-medium text-brand-600">{t.code.replaceAll('_', ' ')}</div>
                {latestRate ? (
                  <div className="mt-2 text-xs text-slate-500">
                    Rate: {scr(latestRate.rate)} {latestRate.unit.toLowerCase()}
                  </div>
                ) : (
                  <div className="mt-2 text-xs text-amber-600">No rate configured yet</div>
                )}
                <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
                  {describeRules(t).slice(0, 3).map((line, i) => (
                    <li key={i}>· {line}</li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="card max-w-xl p-5">
          <button className="mb-3 text-xs font-semibold text-brand-600 hover:underline" onClick={() => setSelected(null)}>
            ← Choose a different benefit
          </button>
          <h2 className="text-base font-bold text-slate-900">{selected.name}</h2>
          <ul className="mt-2 space-y-0.5 text-xs text-slate-500">
            {describeRules(selected).map((line, i) => (
              <li key={i}>· {line}</li>
            ))}
          </ul>
          {selected.evidenceRequired.length > 0 ? (
            <p className="mt-2 text-xs text-slate-500">
              Evidence typically required: {selected.evidenceRequired.join(', ')}
            </p>
          ) : null}

          <div className="mt-4 space-y-3">
            <Field label="Your date of birth">
              <input className="field-input" type="date" value={dob} onChange={(e) => setDob(e.target.value)} />
            </Field>
            <Field label="Evidence references (comma-separated document/DocVault refs)">
              <input
                className="field-input"
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="e.g. DOCV-ASP-2026-0091, employer letter ref"
              />
            </Field>
            <button className="btn-primary w-full" onClick={submit} disabled={busy || !dob}>
              {busy ? 'Submitting…' : `Submit claim for ${selected.name}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
